import {
  amount_to_currency,
  contract_risk_class,
  contract_status_class,
  create_contract_form,
  renewal_badge_from_value
} from './contract_data';
import { contract_drawer_record } from './contract_drawer';

let contract_sequence = 105;

function trim_or_fallback(value, fallback) {
  const trimmed_value = (value || '').trim() || fallback;
  return trimmed_value;
}

function next_contract_id() {
  contract_sequence += 1;
  const contract_id = `K-${contract_sequence}`;
  return contract_id;
}

function build_contract_owner(owner_value) {
  const owner = trim_or_fallback(owner_value, 'Dept - name');
  const [owner_area = 'Legal', owner_short = 'Owner'] = owner.split('-').map((value) => value.trim());
  const contract_owner = { owner, owner_area, owner_short };
  return contract_owner;
}

function resolve_contract_value(form) {
  const contract_value = amount_to_currency(form.value);
  const contract_value_raw = String(form.value || '').trim();
  const resolved_contract_value = { contract_value, contract_value_raw };
  return resolved_contract_value;
}

function get_contract_text(value, fallback) {
  return (value || '').trim() || fallback;
}

function get_risk_rating(form) {
  return Math.max(0, Number(form.risk_rating || 0));
}

function build_contract_row(id, form) {
  const risk_rating = get_risk_rating(form);
  const risk = contract_risk_class(risk_rating);

  const renewal_label = renewal_badge_from_value(form.status);
  const renewal_class = contract_status_class(form.status);

  const { owner, owner_area, owner_short } = build_contract_owner(form.owner);
  const { contract_value, contract_value_raw } = resolve_contract_value(form);

  const contract_summary = {
    row: {
      id,
      contract: get_contract_text(form.contract, 'New contract'),
      supplier: get_contract_text(form.supplier, 'Supplier name'),
      owner,
      owner_short,
      area: owner_area,
      value: contract_value,
      value_raw: contract_value_raw,
      renewal: { label: renewal_label, class: renewal_class },
      renewal_value: form.status,
      risk,
      risk_value: risk_rating
    },
    renewal_label,
    renewal_class,
    risk_rating
  };

  return contract_summary;
}

function renewal_summary_from_label(renewal_label) {
  if (renewal_label === 'Expired') {
    const expired_summary = { days_left: 'Expired', days_class: 'text-danger', icon: 'bi-bell text-danger' };
    return expired_summary;
  }
  if (renewal_label === 'Renewal due') {
    const renewal_due_summary = { days_left: '90d', days_class: 'text-warning-emphasis', icon: 'bi-bell text-warning' };
    return renewal_due_summary;
  }
  const active_summary = { days_left: 'Active', days_class: '', icon: 'bi-bell text-success' };
  return active_summary;
}

function build_renewal_row(id, contract_row, form, renewal_label, renewal_class) {
  const renewal_summary = renewal_summary_from_label(renewal_label);
  const renewal_row = {
    id,
    contract: contract_row.contract,
    supplier: contract_row.supplier,
    owner: contract_row.owner,
    end_date: trim_or_fallback(form.end_date, 'TBC'),
    days_left: renewal_summary.days_left,
    days_class: renewal_summary.days_class,
    status: { label: renewal_label, class: renewal_class },
    icon: renewal_summary.icon
  };
  return renewal_row;
}

function risk_tone_from_label(label) {
  if (label === 'High') {
    const high_tone = 'danger';
    return high_tone;
  }
  if (label === 'Medium') {
    const medium_tone = 'warning';
    return medium_tone;
  }
  const default_tone = 'success';
  return default_tone;
}

function resolve_renewal_tone(days_class) {
  if (days_class === 'text-danger') return 'danger';
  if (days_class) return 'warning';
  return 'success';
}

function build_contract_drawer(
  id,contract_row,form,renewal_label,renewal_class,risk_rating) {
  const renewal_summary = renewal_summary_from_label(renewal_label);
  const contract_drawer = {
    section: `CONTRACT - ${id} - ${(contract_row.area || 'LEGAL').toUpperCase()} - ${(contract_row.owner_short || 'OWNER').toUpperCase()}`,
    title: contract_row.contract,
    subtitle: `${contract_row.value} - ${contract_row.supplier}`,
    status: renewal_label,
    status_class: renewal_class,
    term: `${trim_or_fallback(form.start_date, 'TBC')} - ${trim_or_fallback(form.end_date, 'TBC')}`,
    renewal: renewal_summary.days_left,
    renewal_tone: resolve_renewal_tone(renewal_summary.days_class),
    renewal_label,
    value: contract_row.value,
    risk_value: `${risk_rating}/100`,
    risk_label: contract_row.risk.label,
    risk_tone: risk_tone_from_label(contract_row.risk.label),
    connected_records: [
      contract_drawer_record('Supplier', contract_row.supplier, '', 'teal')
    ],
    obligations: [],
    deliverables: [],
    variations: [],
    approvals: [],
    checklist_progress: '0/0',
    checklist: []
  };

  return contract_drawer;
}

export const contract_methods = {
  set_tab(tab_id) {
    this.active_tab = tab_id;
    this.active_contract_drawer_key = null;
  },
  sync_overlay_state() {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('contracts-overlay-open', this.contract_modal_open || Boolean(this.active_contract_drawer_key));
  },
  open_contract_modal() {
    this.close_contract_drawer();
    this.contract_form = create_contract_form();
    this.contract_modal_open = true;
    this.sync_overlay_state();
  },
  close_contract_modal() {
    this.contract_modal_open = false;
    this.sync_overlay_state();
  },
  open_contract_drawer(contract_or_id) {
    const contract_id = typeof contract_or_id === 'string' ? contract_or_id : contract_or_id?.id;
    if (!contract_id) return;
    this.contract_modal_open = false;
    this.active_contract_drawer_key = contract_id;
    this.sync_overlay_state();
    this.$nextTick(() => {
      const drawer_body = this.$refs.contractDrawerBody;
      if (drawer_body) drawer_body.scrollTop = 0;
    });
  },
  close_contract_drawer() {
    this.active_contract_drawer_key = null;
    this.sync_overlay_state();
  },
  contract_id_for_name(contract_name) {
    return this.contracts.find((row) => row.contract === contract_name)?.id || null;
  },
  drawer_tone_class(tone) {
    const tone_map = { teal: 'is-slate', green: 'is-green', orange: 'is-orange', warning: 'is-orange', red: 'is-red', danger: 'is-red', slate: 'is-slate' };
    const tone_class = Reflect.get(tone_map, tone) || 'is-slate';
    return tone_class;
  },
  drawer_icon_for_type(type) {
    const icon_map = { Supplier: 'bi-truck', Procurement: 'bi-cart', Finance: 'bi-cash-coin', Risk: 'bi-exclamation-triangle', Incident: 'bi-exclamation-octagon', POPIA: 'bi-shield-lock' };
    const drawer_icon = Reflect.get(icon_map, type) || 'bi-circle';
    return drawer_icon;
  },
  submit_contract_modal() {
    const id = next_contract_id();
    const { row: contract_row, renewal_label, renewal_class, risk_rating } = build_contract_row(id, this.contract_form);

    this.contracts.unshift(contract_row);
    this.renewal_rows.unshift(build_renewal_row(id, contract_row, this.contract_form, renewal_label, renewal_class));
    Reflect.set(this.contract_drawers, id, build_contract_drawer(id, contract_row, this.contract_form, renewal_label, renewal_class, risk_rating));

    this.close_contract_modal();
    this.open_contract_drawer(id);
  }
};

export const contract_lifecycle_hooks = {
  mounted() {
    this.sync_overlay_state();
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') document.body.classList.remove('contracts-overlay-open');
  }
};

export const contract_watch = {
  contract_modal_open() {
    this.sync_overlay_state();
  },
  active_contract_drawer_key() {
    this.sync_overlay_state();
  }
};
